import { DateTimeType } from './dateTypes';
import { OtherString } from './otherString';
import { ReceiptType } from './receiptType';
import { z } from 'zod';

export type TransactionLineItemBase = z.infer<typeof TransactionLineItemBase>;
export const TransactionLineItemBase = z.object({
    key: z.string(),
    sum: z.number(),
    tax: z.number(),
    note: z.string().optional(),
});

export type TransactionLineItem = z.infer<typeof TransactionLineItem>;
export const TransactionLineItem = z.discriminatedUnion('type', [
    TransactionLineItemBase.extend({
        type: z.literal('expenses'),
    }),
    TransactionLineItemBase.extend({
        type: z.literal('overnight'),
    }),
    TransactionLineItemBase.extend({
        type: z.literal('receipt'),
        receiptId: z.string(),
        receiptType: ReceiptType.or(OtherString),
        index: z.number(),
        mealParticipantsExternal: z.boolean().optional(),
    }),
]);

export type TransactionStatus = z.infer<typeof TransactionStatus>;
export const TransactionStatus = z.enum(['pending', 'processing', 'completed', 'canceled']);

export type Reconciliation = z.infer<typeof Reconciliation>;
export const Reconciliation = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('payment'),
        amount: z.number(),
    }),
    z.object({
        type: z.literal('bucket'),
        amount: z.number(),
        bucketId: z.string(),
        bucketType: z.enum(['standalone', 'trip']),
        transactionId: z.string(),
    }),
    z.object({
        type: z.literal('other'),
        amount: z.number(),
    }),
]);

export type TransactionAttachment = z.infer<typeof TransactionAttachment>;
export const TransactionAttachment = z.object({
    fileName: z.string(),
});

export type Transaction = z.infer<typeof Transaction>;
export const Transaction = z.object({
    id: z.string(),
    status: TransactionStatus,
    statusReachedOn: z.partialRecord(TransactionStatus, DateTimeType),
    error: z.string().optional(),
    /** When admins were mailed about `error`. Cleared together with `error`. */
    errorNotifiedOn: DateTimeType.optional(),
    lineItems: z.array(TransactionLineItem),
    total: z.number(),
    paidTotal: z.number(),
    processor: z.string(),
    processorData: z.unknown().optional(),
    reconciliation: z.array(Reconciliation).default([]),
    attachments: z
        .object({
            newItems: TransactionAttachment.optional(),
            reversedItems: TransactionAttachment.optional(),
        })
        .default({}),
});

export const immutableTransactionStatuses: readonly TransactionStatus[] = [
    'completed',
    'canceled',
] as const;
